// A PDF laid out page by page on the reader's mat, drawn by pdf.js rather than the browser's own
// viewer. A frame handed the PDF only works where the browser carries a viewer to put in it, which
// most phones' browsers don't: Chrome on Android leaves the frame empty, others save the file
// instead, and Safari on iOS shows the first page alone. Drawing it here reads the same everywhere.
//
// pdf.js is large, so it is only fetched the first time a PDF is opened, and its worker does the
// parsing off the page. The data it reads along the way — fonts, character maps, image decoders —
// is served beside the app (see "vite/pdfData").
//
// Every page holds its place from the start, sized like the first page until its own size is
// known, so the scrollbar is right before anything is drawn. A page is only drawn once it nears
// the screen, at the width it is shown and the screen's sharpness, and is let go again once it is
// well past, so a long PDF on a phone holds a handful of pages in memory rather than all of them.
// It is drawn again when its width changes, as when a phone is turned.
import PropTypes from 'prop-types'
import {useEffect, useRef, useState} from 'react'
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

const DATA = '/pdfjs/'

const READING_DATA = {
  cMapUrl: `${DATA}cmaps/`,
  iccUrl: `${DATA}iccs/`,
  standardFontDataUrl: `${DATA}standard_fonts/`,
  wasmUrl: `${DATA}wasm/`
}

// How far beyond the screen a page is drawn ahead of being scrolled to, and kept after.
const NEARBY = '150% 0px'

// Sharper than this costs memory a phone notices without looking any different.
const MOST_SHARPNESS = 2
// Below the largest canvas iOS Safari will draw, which leaves one beyond it blank.
const MOST_CANVAS_AREA = 16 * 1000 * 1000

let library = null

const openLibrary = () => {
  library ??= import('pdfjs-dist')
    .then(pdfjs => {
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl

      return pdfjs
    })
    .catch(error => {
      library = null
      throw error
    })

  return library
}

const shapeOf = page => {
  const {width, height} = page.getViewport({scale: 1})

  return width / height
}

const useNear = element => {
  const [near, setNear] = useState(false)

  useEffect(() => {
    if (!element.current || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(entries => setNear(entries.at(-1).isIntersecting), {rootMargin: NEARBY})

    observer.observe(element.current)

    return () => observer.disconnect()
  }, [])

  return near
}

const useWidth = element => {
  const [width, setWidth] = useState(0)

  useEffect(() => {
    if (!element.current || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(entries => setWidth(Math.round(entries.at(-1).contentRect.width)))

    observer.observe(element.current)

    return () => observer.disconnect()
  }, [])

  return width
}

const scaleFor = (page, width) => {
  const {width: pageWidth, height: pageHeight} = page.getViewport({scale: 1})
  const sharpness = Math.min(window.devicePixelRatio || 1, MOST_SHARPNESS)
  const scale = width * sharpness / pageWidth

  return Math.min(scale, Math.sqrt(MOST_CANVAS_AREA / (pageWidth * pageHeight)))
}

const letGo = canvas => {
  canvas.width = 0
  canvas.height = 0
}

const PdfPage = ({pdf, number, shape}) => {
  const sheet = useRef(null)
  const canvas = useRef(null)
  const [ownShape, setOwnShape] = useState(null)
  const near = useNear(sheet)
  const width = useWidth(sheet)

  useEffect(() => {
    if (!near) return letGo(canvas.current)
    if (!width) return

    let current = true
    let drawing = null

    pdf.getPage(number)
      .then(page => {
        if (!current) return

        const viewport = page.getViewport({scale: scaleFor(page, width)})

        setOwnShape(shapeOf(page))
        canvas.current.width = Math.floor(viewport.width)
        canvas.current.height = Math.floor(viewport.height)
        drawing = page.render({canvas: canvas.current, viewport})

        return drawing.promise
      })
      // A page that can't be drawn, or is let go of mid-draw, is left as blank paper.
      .catch(() => {})

    return () => {
      current = false
      drawing?.cancel()
    }
  }, [pdf, number, near, width])

  return (
    <div ref={sheet} className='pdf-page' style={{aspectRatio: ownShape ?? shape}} aria-label={`Page ${number}`} role='img'>
      <canvas ref={canvas} />
    </div>
  )
}

PdfPage.propTypes = {
  pdf: PropTypes.object.isRequired,
  number: PropTypes.number.isRequired,
  shape: PropTypes.number.isRequired
}

/**
 * Lays out the PDF in "bytes", titled "title" for whoever is reading it out. Calls "onFailed" with
 * "locked" when the PDF asks for a password, or "unreadable" when it can't be opened at all.
 */
const PdfPages = ({bytes, title, onFailed}) => {
  const [opened, setOpened] = useState(null)

  useEffect(() => {
    let current = true
    let loading = null

    openLibrary()
      .then(pdfjs => {
        if (!current) return null

        // pdf.js hands the bytes over to its worker, which leaves them empty here, so it is given
        // its own copy and the preview can open them again.
        loading = pdfjs.getDocument({data: bytes.slice(), ...READING_DATA})

        return loading.promise
      })
      .then(async pdf => {
        if (!current || !pdf) return

        const shape = shapeOf(await pdf.getPage(1))

        if (current) setOpened({pdf, shape})
      })
      .catch(error => current && onFailed(error?.name === 'PasswordException' ? 'locked' : 'unreadable'))

    return () => {
      current = false
      loading?.destroy()
    }
  }, [bytes])

  if (!opened) {
    return (
      <div className='preview-opening' role='status'>
        <span className='opening-spinner' />
        Laying out the pages…
      </div>
    )
  }

  return (
    <div className='preview-pdf' role='document' aria-label={title}>
      {Array.from({length: opened.pdf.numPages}, (page, index) => (
        <PdfPage key={index} pdf={opened.pdf} number={index + 1} shape={opened.shape} />
      ))}
    </div>
  )
}

PdfPages.propTypes = {
  bytes: PropTypes.instanceOf(Uint8Array).isRequired,
  title: PropTypes.string.isRequired,
  onFailed: PropTypes.func.isRequired
}

export default PdfPages
